import { useState } from 'react'
import { useSharing } from '../../hooks/useSharing'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const EXPIRY_OPTIONS = [
  { label: 'Never expires', value: null },
  { label: '1 day',         value: 1 },
  { label: '7 days',        value: 7 },
  { label: '30 days',       value: 30 },
  { label: '90 days',       value: 90 },
]

/**
 * ShareModal
 *
 * Props:
 *   type   — 'document' | 'specification' | 'test-plan'
 *   id     — artifact id
 *   title  — artifact title string
 *   onClose — () => void
 */
export default function ShareModal({ type, id, title, onClose }) {
  const { data, loading, working, enable, disable, revoke, updateExpiry } = useSharing(type, id)
  const [expiresInDays, setExpiresInDays] = useState(null)
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    if (!data?.share_url) return
    await navigator.clipboard.writeText(data.share_url)
    setCopied(true)
    toast.success('Link copied!')
    setTimeout(() => setCopied(false), 2500)
  }

  const handleEnable = async () => {
    const result = await enable(expiresInDays)
    if (result.ok) toast.success('Sharing enabled!')
    else toast.error('Failed to enable sharing.')
  }

  const handleDisable = async () => {
    const result = await disable()
    if (result.ok) toast.success('Sharing disabled.')
    else toast.error('Failed to disable sharing.')
  }

  const handleRevoke = async () => {
    if (!confirm('Revoke the link? Anyone with the current link will lose access.')) return
    const result = await revoke()
    if (result.ok) toast.success('Link revoked. A new one will be generated next time you enable sharing.')
    else toast.error('Failed to revoke link.')
  }

  const handleExpiryChange = async (days) => {
    setExpiresInDays(days)
    if (data?.is_public) {
      await updateExpiry(days)
      toast.success('Expiry updated.')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md glass-card rounded-2xl overflow-hidden shadow-2xl animate-fade-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-white/8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-display font-bold text-white text-base">Share Document</h2>
              <p className="text-xs text-white/40 mt-0.5 truncate max-w-xs">{title}</p>
            </div>
            <button onClick={onClose} className="text-white/30 hover:text-white transition-colors mt-0.5">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="p-6 space-y-5">

            {/* Status toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/8">
              <div>
                <p className="text-sm font-semibold text-white">Public link</p>
                <p className="text-xs text-white/40 mt-0.5">
                  {data?.is_share_active
                    ? 'Anyone with the link can view this document.'
                    : 'Only you can access this document.'}
                </p>
              </div>
              <button
                disabled={working}
                onClick={data?.is_share_active ? handleDisable : handleEnable}
                className={clsx(
                  'relative w-12 h-6 rounded-full transition-all duration-300 flex-shrink-0',
                  'focus:outline-none focus:ring-2 focus:ring-purple-500/30',
                  data?.is_share_active ? 'bg-purple-500' : 'bg-white/15',
                  working && 'opacity-50 cursor-wait'
                )}
              >
                <div className={clsx(
                  'absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all duration-300',
                  data?.is_share_active ? 'left-7' : 'left-1'
                )} />
              </button>
            </div>

            {/* Share URL */}
            {data?.is_share_active && (
              <div className="space-y-2 animate-fade-in">
                <label className="label-specze">Share Link</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 flex items-center gap-2 bg-purple-500/8 border border-purple-500/20 rounded-xl px-3.5 py-2.5 min-w-0">
                    <svg className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                    </svg>
                    <code className="text-xs text-purple-300 font-mono truncate flex-1">{data?.share_url}</code>
                  </div>
                  <button
                    onClick={copy}
                    className={clsx(
                      'flex-shrink-0 px-4 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-200',
                      copied
                        ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                        : 'bg-purple-500/15 border-purple-500/30 text-purple-300 hover:bg-purple-500/25'
                    )}
                  >
                    {copied ? '✓ Copied!' : 'Copy'}
                  </button>
                </div>

                {/* View stats */}
                <div className="flex items-center gap-4 px-1">
                  <span className="text-[11px] text-white/30">
                    👁 {data?.view_count ?? 0} view{data?.view_count !== 1 ? 's' : ''}
                  </span>
                  <span className="text-[11px] text-white/30">
                    📄 Version {data?.version ?? 1}
                  </span>
                  {data?.share_expires_at && (
                    <span className="text-[11px] text-amber-400/70">
                      ⏱ Expires {new Date(data.share_expires_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Expiry selector */}
            <div>
              <label className="label-specze">Link expiry</label>
              <div className="flex flex-wrap gap-2">
                {EXPIRY_OPTIONS.map(opt => (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => handleExpiryChange(opt.value)}
                    className={clsx(
                      'px-3 py-1.5 rounded-lg text-xs font-medium border transition-all',
                      expiresInDays === opt.value
                        ? 'bg-purple-500/20 border-purple-500/40 text-purple-300'
                        : 'bg-white/4 border-white/10 text-white/50 hover:border-white/20 hover:text-white'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Revoke */}
            {data?.share_token && (
              <div className="pt-2 border-t border-white/6">
                <button
                  onClick={handleRevoke}
                  disabled={working}
                  className="text-xs text-red-400/70 hover:text-red-400 transition-colors flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                  </svg>
                  Revoke link and generate a new one
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
